import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search, BookOpen, ChevronRight, ArrowLeft, Clock, Eye, Tag,
  Plus, Pencil, Trash2, Save, Layers, Sparkles, ThumbsUp, ThumbsDown,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import Layout from "../components/Layout";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const CATEGORY_COLORS = {
  "Getting Started":        "bg-blue-100 text-blue-700 border-blue-200",
  "Leads":                  "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Site Visits":            "bg-purple-100 text-purple-700 border-purple-200",
  "Deal Rooms":             "bg-amber-100 text-amber-700 border-amber-200",
  "Negotiations":           "bg-rose-100 text-rose-700 border-rose-200",
  "Analytics":              "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Workflows":              "bg-orange-100 text-orange-700 border-orange-200",
  "Inventory":              "bg-teal-100 text-teal-700 border-teal-200",
  "Admin & Settings":       "bg-slate-100 text-slate-700 border-slate-200",
  "Nurture & AI":           "bg-indigo-100 text-indigo-700 border-indigo-200",
  "Automations & Messaging":"bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  "Routing & Teams":        "bg-lime-100 text-lime-700 border-lime-200",
  "Channel Partners":       "bg-yellow-100 text-yellow-700 border-yellow-200",
  "Integrations & Webhooks":"bg-sky-100 text-sky-700 border-sky-200",
  "Governance & Approvals": "bg-red-100 text-red-700 border-red-200",
};

const HelpCenterPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user && ["admin", "ceo", "hod_sales", "sales_head"].includes(user.role);

  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // AI search
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState(null);
  const [aiSources, setAiSources] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  // Feedback
  const [feedbackBusy, setFeedbackBusy] = useState(false);

  // Editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editorForm, setEditorForm] = useState({
    title: "", content: "", summary: "", category: "Getting Started", tags: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchArticle = useCallback(async (articleId) => {
    try {
      const res = await axios.get(`${API}/help/articles/${articleId}`);
      setSelectedArticle(res.data);
    } catch { toast.error("Failed to load article"); }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  // Deep link: /help?article=xxx
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const articleId = params.get("article");
    if (articleId) fetchArticle(articleId);
  }, [location.search, fetchArticle]);

  useEffect(() => {
    fetchArticles();
  }, [selectedCategory, search]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/help/categories`);
      setCategories(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchArticles = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append("category", selectedCategory);
      if (search) params.append("search", search);
      const res = await axios.get(`${API}/help/articles?${params.toString()}`);
      setArticles(res.data);
    } catch { /* ignore */ }
  };

  const handleSeedArticles = async () => {
    try {
      const res = await axios.post(`${API}/help/seed-v2`);
      const { created, updated, legacy_purged: purged } = res.data;
      const parts = [`${created} new`, `${updated} refreshed`];
      if (purged) parts.push(`${purged} legacy removed`);
      toast.success(`Seeded: ${parts.join(" · ")}`);
      fetchCategories();
      fetchArticles();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to seed"); }
  };

  const handleAskAi = async () => {
    if (!aiQuestion.trim()) return;
    setAiLoading(true);
    setAiAnswer(null);
    setAiSources([]);
    try {
      const res = await axios.post(`${API}/help/ai-search`, { question: aiQuestion.trim() });
      setAiAnswer(res.data.answer);
      setAiSources(res.data.sources || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI search failed");
    }
    setAiLoading(false);
  };

  const handleFeedback = async (helpful) => {
    if (!selectedArticle) return;
    setFeedbackBusy(true);
    try {
      await axios.post(
        `${API}/help/articles/${selectedArticle.article_id}/feedback`,
        { helpful },
      );
      toast.success("Thanks for the feedback!");
      setSelectedArticle(a => ({
        ...a,
        helpful_count: (a.helpful_count || 0) + (helpful ? 1 : 0),
        not_helpful_count: (a.not_helpful_count || 0) + (helpful ? 0 : 1),
      }));
    } catch { toast.error("Could not record feedback"); }
    setFeedbackBusy(false);
  };

  const handleSaveArticle = async () => {
    if (!editorForm.title.trim() || !editorForm.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editorForm,
        tags: editorForm.tags.split(",").map(t => t.trim()).filter(Boolean),
      };
      if (editingArticle) {
        await axios.put(`${API}/help/articles/${editingArticle.article_id}`, payload);
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/help/articles`, payload);
        toast.success("Article created");
      }
      setEditorOpen(false);
      setEditingArticle(null);
      fetchCategories();
      fetchArticles();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  const handleDeleteArticle = async (articleId) => {
    try {
      await axios.delete(`${API}/help/articles/${articleId}`);
      toast.success("Article deleted");
      setSelectedArticle(null);
      fetchCategories();
      fetchArticles();
    } catch { toast.error("Failed to delete"); }
  };

  const openEditor = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setEditorForm({
        title: article.title,
        content: article.content,
        summary: article.summary || "",
        category: article.category,
        tags: (article.tags || []).join(", "),
      });
    } else {
      setEditingArticle(null);
      setEditorForm({
        title: "", content: "", summary: "",
        category: selectedCategory || "Getting Started", tags: "",
      });
    }
    setEditorOpen(true);
  };

  // Markdown-ish render
  const renderContent = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i) => {
      if (line.startsWith("### ")) return <h3 key={i} className="text-base font-semibold text-slate-800 mt-4 mb-1 font-['Manrope']">{line.slice(4)}</h3>;
      if (line.startsWith("## ")) return <h2 key={i} className="text-lg font-bold text-slate-900 mt-5 mb-2 font-['Manrope']">{line.slice(3)}</h2>;
      if (line.startsWith("- **")) {
        const match = line.match(/^- \*\*(.+?)\*\*\s*[—–-]?\s*(.*)/);
        if (match) return <li key={i} className="ml-4 text-sm text-slate-600 mb-1"><span className="font-semibold text-slate-800">{match[1]}</span>{match[2] ? ` — ${match[2]}` : ""}</li>;
      }
      if (line.startsWith("- ")) return <li key={i} className="ml-4 text-sm text-slate-600 mb-1">{line.slice(2)}</li>;
      if (/^\d+\.\s/.test(line)) return <li key={i} className="ml-4 text-sm text-slate-600 mb-1 list-decimal">{line.replace(/^\d+\.\s/, "")}</li>;
      if (line.trim() === "") return <div key={i} className="h-2" />;
      const parts = line.split(/\*\*(.+?)\*\*/g);
      return (
        <p key={i} className="text-sm text-slate-600 mb-1">
          {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-slate-800">{part}</strong> : part)}
        </p>
      );
    });
  };

  // Article Detail View
  if (selectedArticle) {
    return (
      <Layout>
        <div className="p-6 max-w-4xl mx-auto" data-testid="help-article-detail">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedArticle(null);
              if (location.search) navigate("/help", { replace: true });
            }}
            className="mb-4"
            data-testid="back-to-help"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Help Center
          </Button>
          <Card className="bg-white border-slate-200">
            <CardContent className="p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <Badge className={`${CATEGORY_COLORS[selectedArticle.category] || ""} text-xs mb-2`}>{selectedArticle.category}</Badge>
                  <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">{selectedArticle.title}</h1>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditor(selectedArticle)} data-testid="edit-article-btn">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteArticle(selectedArticle.article_id)} className="text-red-500 hover:text-red-700" data-testid="delete-article-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-400 mb-6 pb-4 border-b border-slate-100">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(selectedArticle.updated_at).toLocaleDateString()}</span>
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {selectedArticle.views || 0} views</span>
                {selectedArticle.author_name && <span>By {selectedArticle.author_name}</span>}
              </div>
              <div className="prose-sm" data-testid="article-content">
                {renderContent(selectedArticle.content)}
              </div>
              {(selectedArticle.tags || []).length > 0 && (
                <div className="flex items-center gap-2 mt-8 pt-4 border-t border-slate-100 flex-wrap">
                  <Tag className="w-3.5 h-3.5 text-slate-400" />
                  {selectedArticle.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs text-slate-500">{tag}</Badge>
                  ))}
                </div>
              )}

              {/* Feedback */}
              <div className="mt-8 pt-4 border-t border-slate-100 flex items-center gap-3 flex-wrap" data-testid="article-feedback">
                <span className="text-sm text-slate-600">Was this helpful?</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={feedbackBusy}
                  onClick={() => handleFeedback(true)}
                  data-testid="feedback-helpful-btn"
                >
                  <ThumbsUp className="w-4 h-4 mr-1" /> Yes
                  {selectedArticle.helpful_count ? (
                    <span className="ml-2 text-xs text-slate-500">{selectedArticle.helpful_count}</span>
                  ) : null}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={feedbackBusy}
                  onClick={() => handleFeedback(false)}
                  data-testid="feedback-not-helpful-btn"
                >
                  <ThumbsDown className="w-4 h-4 mr-1" /> No
                  {selectedArticle.not_helpful_count ? (
                    <span className="ml-2 text-xs text-slate-500">{selectedArticle.not_helpful_count}</span>
                  ) : null}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <ArticleEditor
          open={editorOpen}
          onOpenChange={setEditorOpen}
          form={editorForm}
          setForm={setEditorForm}
          onSave={handleSaveArticle}
          saving={saving}
          isNew={!editingArticle}
        />
      </Layout>
    );
  }

  // Main view
  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="help-center">
        <div className="text-center py-6">
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">Help Center</h1>
          <p className="text-slate-500 mt-2">Find answers, guides, and tips for every CRM feature</p>
          <div className="relative max-w-lg mx-auto mt-5">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search articles by title, tag, or keyword…"
              value={search}
              onChange={e => { setSearch(e.target.value); setSelectedCategory(null); }}
              className="pl-12 h-12 text-base rounded-xl border-slate-200 shadow-sm"
              data-testid="help-search"
            />
          </div>
        </div>

        {/* AI Search */}
        <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-100" data-testid="ai-search-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-indigo-900 font-['Manrope']">Ask AI</h2>
              <span className="text-xs text-indigo-700/70">Get a cited answer across the entire knowledge base</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder='e.g. "How do I reroute leads from a deactivated user?"'
                value={aiQuestion}
                onChange={e => setAiQuestion(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleAskAi(); }}
                className="bg-white border-indigo-200"
                data-testid="ai-question-input"
              />
              <Button
                onClick={handleAskAi}
                disabled={aiLoading || !aiQuestion.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="ai-ask-btn"
              >
                {aiLoading ? "Thinking…" : "Ask"}
              </Button>
            </div>
            {aiAnswer && (
              <div className="mt-4 bg-white rounded-lg border border-indigo-100 p-4" data-testid="ai-answer">
                <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2">Answer</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{aiAnswer}</div>
                {aiSources.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="text-xs font-semibold text-slate-500 mb-2">Sources</div>
                    <div className="flex flex-wrap gap-2">
                      {aiSources.map((s, i) => (
                        <button
                          key={s.article_id}
                          type="button"
                          onClick={() => fetchArticle(s.article_id)}
                          className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                          data-testid={`ai-source-${i}`}
                        >
                          [{i + 1}] {s.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin Actions */}
        {isAdmin && (
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleSeedArticles} data-testid="seed-articles-btn">
              <Layers className="w-4 h-4 mr-1" /> Seed / Refresh Library
            </Button>
            <Button size="sm" onClick={() => openEditor()} data-testid="create-article-btn">
              <Plus className="w-4 h-4 mr-1" /> New Article
            </Button>
          </div>
        )}

        {/* Categories or Article List */}
        {!search && !selectedCategory ? (
          <div>
            <h2 className="text-lg font-semibold text-slate-800 font-['Manrope'] mb-4">Browse by Topic</h2>
            {loading ? (
              <div className="text-center py-8 text-slate-400">Loading…</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {categories.map(cat => (
                  <Card
                    key={cat.name}
                    className={`border cursor-pointer hover:shadow-md transition-shadow ${CATEGORY_COLORS[cat.name] || "bg-slate-50 border-slate-200"}`}
                    onClick={() => setSelectedCategory(cat.name)}
                    data-testid={`category-${cat.name.toLowerCase().replace(/[& ]+/g, "-")}`}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-sm">{cat.name}</p>
                        <p className="text-xs opacity-70">{cat.count} article{cat.count !== 1 ? "s" : ""}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 opacity-50" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {selectedCategory && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedCategory(null)} data-testid="clear-category">
                    <ArrowLeft className="w-4 h-4 mr-1" /> All Topics
                  </Button>
                )}
                <h2 className="text-lg font-semibold text-slate-800 font-['Manrope']">
                  {search ? `Search results for "${search}"` : selectedCategory}
                </h2>
              </div>
              <span className="text-sm text-slate-400">{articles.length} article{articles.length !== 1 ? "s" : ""}</span>
            </div>

            {articles.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No articles found</p>
                {isAdmin && <p className="text-sm text-slate-400 mt-1">Use "Seed / Refresh Library" to load the starter article set</p>}
              </div>
            ) : (
              <div className="space-y-2">
                {articles.map(article => (
                  <Card
                    key={article.article_id}
                    className="bg-white border-slate-200 hover:shadow-sm hover:border-slate-300 transition-all cursor-pointer"
                    onClick={() => fetchArticle(article.article_id)}
                    data-testid={`article-card-${article.article_id}`}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`${CATEGORY_COLORS[article.category] || ""} text-xs`}>{article.category}</Badge>
                          <h3 className="font-medium text-slate-800 text-sm truncate">{article.title}</h3>
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-1">{article.summary}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <ArticleEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        form={editorForm}
        setForm={setEditorForm}
        onSave={handleSaveArticle}
        saving={saving}
        isNew={!editingArticle}
      />
    </Layout>
  );
};

const ArticleEditor = ({ open, onOpenChange, form, setForm, onSave, saving, isNew }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="article-editor-dialog">
      <DialogHeader>
        <DialogTitle className="font-['Manrope']">{isNew ? "New Article" : "Edit Article"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Title</Label>
          <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Article title" data-testid="article-title-input" />
        </div>
        <div>
          <Label>Category</Label>
          <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
            <SelectTrigger data-testid="article-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.keys(CATEGORY_COLORS).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Summary <span className="text-xs text-slate-400">(shown in article list)</span></Label>
          <Input value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} placeholder="Brief description…" data-testid="article-summary-input" />
        </div>
        <div>
          <Label>Content <span className="text-xs text-slate-400">(supports ## headings, **bold**, - lists)</span></Label>
          <Textarea
            value={form.content}
            onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            rows={12}
            placeholder="Write your article content here…"
            className="font-mono text-sm"
            data-testid="article-content-input"
          />
        </div>
        <div>
          <Label>Tags <span className="text-xs text-slate-400">(comma-separated)</span></Label>
          <Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="e.g. leads, import, scoring" data-testid="article-tags-input" />
        </div>
        <Button onClick={onSave} disabled={saving} className="w-full" data-testid="save-article-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save Article"}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

export default HelpCenterPage;
